import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react'
import { categoryLabel, formatMoney, serviceCover, type NailService } from './catalog'
import './motion-showcase.css'

interface Props {
  services: NailService[]
  onOpenProduct: (id: string) => void
}

const MAX_ITEMS = 8

const getReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function MotionShowcase({ services, onOpenProduct }: Props) {
  const featured = useMemo(() => {
    return services
      .filter((service) => service.enabled && Boolean(serviceCover(service)))
      .slice(0, MAX_ITEMS)
  }, [services])

  const [activeIndex, setActiveIndex] = useState(0)
  const [manualVersion, setManualVersion] = useState(0)
  const [brokenSrc, setBrokenSrc] = useState<Record<string, boolean>>({})
  const [isPaused, setIsPaused] = useState(false)
  const [inView, setInView] = useState(false)
  const [tabVisible, setTabVisible] = useState(true)
  const [focusWithin, setFocusWithin] = useState(false)
  const [touching, setTouching] = useState(false)
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => getReducedMotion())
  const [lightboxId, setLightboxId] = useState<string | null>(null)
  const [lightboxImage, setLightboxImage] = useState('')
  const [lightboxFailed, setLightboxFailed] = useState(false)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)
  const scrollLockRef = useRef<string>('')
  const swipeRef = useRef<{
    x: number
    y: number
    lastX: number
    lastY: number
  } | null>(null)
  const preventClickRef = useRef(false)
  const clickTimeoutRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)

  const total = featured.length
  const active = total > 0 ? featured[Math.min(activeIndex, total - 1)] : null
  const isSingle = total <= 1

  useEffect(() => {
    setActiveIndex((index) => (total === 0 ? 0 : Math.min(index, total - 1)))
  }, [total])

  const goto = useCallback(
    (index: number) => {
      if (total === 0) return
      setActiveIndex(((index % total) + total) % total)
      setManualVersion((v) => v + 1)
    },
    [total]
  )

  const goPrev = useCallback(() => {
    goto(activeIndex - 1)
  }, [activeIndex, goto])
  const goNext = useCallback(() => {
    goto(activeIndex + 1)
  }, [activeIndex, goto])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReducedMotion(mq.matches)
    update()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update)
      return () => mq.removeEventListener('change', update)
    }
    mq.addListener(update)
    return () => mq.removeListener(update)
  }, [])

  useEffect(() => {
    const node = rootRef.current
    if (!node) {
      setInView(false)
      return
    }
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInView(entry.intersectionRatio >= 0.25)
        })
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [total])

  useEffect(() => {
    const onVisibility = () => setTabVisible(!document.hidden)
    setTabVisible(!document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  const shouldAutoplay =
    total > 1 &&
    !isPaused &&
    !reducedMotion &&
    inView &&
    tabVisible &&
    !touching &&
    !focusWithin &&
    !lightboxId

  useEffect(() => {
    if (!shouldAutoplay || total <= 1) return
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % total)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [shouldAutoplay, total, activeIndex, manualVersion])

  const openLightbox = useCallback(() => {
    if (!active) return
    const src = serviceCover(active)
    if (!src) return
    lastFocusRef.current = (document.activeElement as HTMLElement) ?? null
    setLightboxId(active.id)
    setLightboxImage(src)
    setLightboxFailed(false)
  }, [active])

  const closeLightbox = useCallback(() => {
    setLightboxId(null)
    setLightboxFailed(false)
    const node = lastFocusRef.current
    if (node && typeof node.focus === 'function') {
      window.requestAnimationFrame(() => node.focus({ preventScroll: true }))
    }
  }, [])

  useEffect(() => {
    if (!lightboxId) return
    const dialog = dialogRef.current
    if (!dialog) return
    if (typeof dialog.showModal !== 'function') {
      closeLightbox()
      return
    }
    const previousOverflow = document.body.style.overflow
    scrollLockRef.current = previousOverflow
    document.body.style.overflow = 'hidden'
    if (!dialog.open) dialog.showModal()
    return () => {
      if (dialog.open) dialog.close()
      document.body.style.overflow = scrollLockRef.current
    }
  }, [lightboxId, closeLightbox])

  const handleImageError = useCallback((src: string) => {
    setBrokenSrc((prev) => ({ ...prev, [src]: true }))
  }, [])

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current !== null) window.clearTimeout(clickTimeoutRef.current)
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current)
    }
  }, [])

  const lightboxService = lightboxId
    ? featured.find((item) => item.id === lightboxId) ?? null
    : null

  useEffect(() => {
    if (lightboxId && !lightboxService) closeLightbox()
  }, [lightboxId, lightboxService, closeLightbox])

  if (total === 0) return null

  const image = active ? serviceCover(active) : ''
  const showImage = Boolean(image) && !brokenSrc[image]

  const clearClickSuppression = () => {
    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
    }
    preventClickRef.current = false
  }

  const scheduleClickSuppressionRelease = () => {
    if (clickTimeoutRef.current !== null) window.clearTimeout(clickTimeoutRef.current)
    preventClickRef.current = true
    clickTimeoutRef.current = window.setTimeout(() => {
      preventClickRef.current = false
      clickTimeoutRef.current = null
    }, 400)
  }

  const cancelRaf = () => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }

  const onTouchStart = (event: ReactTouchEvent) => {
    cancelRaf()
    clearClickSuppression()
    if (event.touches.length !== 1) {
      setTouching(false)
      swipeRef.current = null
      return
    }
    setTouching(true)
    const touch = event.touches[0]
    swipeRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
    }
  }

  const onTouchMove = (event: ReactTouchEvent) => {
    const start = swipeRef.current
    if (!start) return
    if (event.touches.length !== 1) {
      setTouching(false)
      swipeRef.current = null
      return
    }
    const touch = event.touches[0]
    start.lastX = touch.clientX
    start.lastY = touch.clientY
  }

  const evaluateGestureEnd = (x: number, y: number) => {
    const start = swipeRef.current
    swipeRef.current = null
    if (!start) return
    const dx = x - start.x
    const dy = y - start.y
    const absX = Math.abs(dx)
    const absY = Math.abs(dy)
    if (absX < 10 && absY < 10) {
      if (active) {
        const src = serviceCover(active)
        if (src && !brokenSrc[src]) {
          openLightbox()
          scheduleClickSuppressionRelease()
        }
      }
      return
    }
    if (isSingle) return
    if (absX > 50 && absX > absY) {
      scheduleClickSuppressionRelease()
      const direction = dx < 0 ? 1 : -1
      cancelRaf()
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null
        if (direction > 0) goNext()
        else goPrev()
      })
    }
  }

  const onTouchEnd = (event: ReactTouchEvent) => {
    setTouching(false)
    const changed = event.changedTouches
    if (!changed || changed.length === 0) {
      swipeRef.current = null
      return
    }
    const touch = changed[0]
    const x = touch.clientX
    const y = touch.clientY
    evaluateGestureEnd(x, y)
  }

  const onTouchCancel = () => {
    setTouching(false)
    swipeRef.current = null
  }

  const handleImageClick = (event: ReactMouseEvent) => {
    if (preventClickRef.current && event.detail !== 0) return
    openLightbox()
  }

  return (
    <div
      className={`motion-showcase${reducedMotion ? ' motion-showcase--reduced' : ''}`}
      ref={rootRef}
      onFocus={(event) => {
        const target = event.target
        if (
          target instanceof HTMLElement &&
          typeof target.matches === 'function' &&
          !target.matches(':focus-visible')
        ) {
          return
        }
        setFocusWithin(true)
      }}
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null
        if (next && event.currentTarget.contains(next)) return
        setFocusWithin(false)
      }}
      onPointerDown={(event) => {
        if (event.pointerType !== 'mouse') return
        const target = event.target
        if (target instanceof HTMLElement && target.matches(':focus-visible')) return
        setFocusWithin(false)
      }}
    >
      <div className="motion-showcase__stage">
        <div
          className="motion-showcase__media"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
        >
          {showImage && active && image ? (
            <button
              type="button"
              className="motion-showcase__photo"
              onClick={handleImageClick}
              aria-label={`放大查看${active.name}图片`}
            >
              <img
                key={active.id}
                className="motion-showcase__img"
                src={image}
                alt={active.name}
                onError={() => handleImageError(image)}
              />
            </button>
          ) : active ? (
            <div
              className="motion-showcase__placeholder"
              role="img"
              aria-label={`${active.name}图片暂时无法显示`}
            >
              <span>汀</span>
              <small>图片暂时无法显示</small>
            </div>
          ) : null}
        </div>

        {active && (
          <div className="motion-showcase__info">
            <span className="motion-showcase__eyebrow">{categoryLabel(active)}</span>
            <h3 className="motion-showcase__name">{active.name}</h3>
            <p className="motion-showcase__desc">{active.description}</p>
            <div className="motion-showcase__meta">
              <strong>{formatMoney(active.price)}</strong>
              <span>{active.durationMinutes} 分钟</span>
            </div>
            <div className="motion-showcase__actions">
              <button
                type="button"
                className="motion-showcase__cta"
                onClick={() => onOpenProduct(active.id)}
              >
                查看商品详情
              </button>
              {!isSingle && (
                <button
                  type="button"
                  className="motion-showcase__toggle"
                  aria-pressed={isPaused}
                  onClick={() => {
                    if (reducedMotion) return
                    if (isPaused) {
                      setFocusWithin(false)
                      setIsPaused(false)
                    } else {
                      setIsPaused(true)
                    }
                  }}
                  disabled={reducedMotion}
                >
                  {reducedMotion
                    ? '已减少动态效果'
                    : isPaused
                    ? '播放轮播'
                    : '暂停轮播'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {!isSingle && (
        <div className="motion-showcase__controls">
          <div className="motion-showcase__nav">
            <button type="button" aria-label="上一个商品" onClick={goPrev}>
              ←
            </button>
            <span
              className="motion-showcase__counter"
              aria-live={shouldAutoplay ? 'off' : 'polite'}
            >
              {Math.min(activeIndex + 1, total)} / {total}
            </span>
            <button type="button" aria-label="下一个商品" onClick={goNext}>
              →
            </button>
          </div>
          <div className="motion-showcase__dots" role="group" aria-label="商品切换">
            {featured.map((service, index) => {
              const label = `切换到第${index + 1}件商品：${service.name}`
              return (
                <button
                  key={service.id}
                  type="button"
                  aria-pressed={index === activeIndex}
                  aria-label={label}
                  className={`motion-showcase__dot${index === activeIndex ? ' is-active' : ''}`}
                  onClick={() => {
                    goto(index)
                  }}
                />
              )
            })}
          </div>
        </div>
      )}

      {lightboxId && (
        <dialog
          ref={dialogRef}
          className="motion-lightbox"
          aria-label={lightboxService ? `${lightboxService.name}大图预览` : '大图预览'}
          onCancel={(event) => {
            event.preventDefault()
            closeLightbox()
          }}
          onClick={(event) => {
            if (preventClickRef.current && event.detail !== 0) return
            if (event.target === event.currentTarget) closeLightbox()
          }}
        >
          <button
            type="button"
            className="motion-lightbox__close"
            aria-label="关闭图片预览"
            onClick={closeLightbox}
          >
            ×
          </button>
          {lightboxFailed || !lightboxImage ? (
            <div className="motion-lightbox__placeholder">图片暂时无法显示</div>
          ) : (
            <img
              className="motion-lightbox__img"
              src={lightboxImage}
              alt={lightboxService ? `${lightboxService.name}大图` : '商品大图'}
              onError={() => setLightboxFailed(true)}
            />
          )}
        </dialog>
      )}
    </div>
  )
}
